import { Card, CardContent, CardHeader } from '@/app/common/components/Card.tsx'
import { CircleLoader } from '@/app/common/components/CircleLoader'
import { parseRequestError } from '@/app/common/utilities.ts'
import { useUpdateProgramPurse } from '@/queries/purse_configs/useUpdateProgramPurse.ts'
import { PURSE_KEYS } from '@/queries/program_purses/keys.ts'
import { useToast } from '@/shad/ui/toast/use-toast.ts'
import { Switch } from '@/shad/ui/switch'
import { useQueryClient } from '@tanstack/react-query'
import { PartialPurseData } from '@/pages/program_purse_settings/purse-details/_components/distribution-settings-options/types.ts'
import { useState } from 'react'
import { Button } from '@/app/common/components/button/Button'
import usePurseAffiliationsQuery from '@/queries/program_purses/usePurseAffiliationsQuery'
import ManageAffiliationsDialog from './ManageAffiliationsDialog'

type Props = {
  partialPurseData: PartialPurseData
}

export default function AffiliationRestrictionCard({
  partialPurseData,
}: Props) {
  const updateMutation = useUpdateProgramPurse()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [manageDialogOpen, setManageDialogOpen] = useState(false)

  const purseAffiliationsQuery = usePurseAffiliationsQuery({
    programId: partialPurseData.programId,
    purseId: partialPurseData.id,
  })

  const hasLoadedCurrentAffiliations =
    purseAffiliationsQuery.status === 'success'

  const currentAffiliations = hasLoadedCurrentAffiliations
    ? purseAffiliationsQuery.data.affiliations
    : []

  const handleUpdate = (checked: boolean) => {
    updateMutation.mutate(
      {
        params: {
          program_id: partialPurseData.programId,
          purse_id: partialPurseData.id,
        },
        payload: { affiliation_restriction_enabled: checked },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: PURSE_KEYS.PURSE_DETAILS(
              partialPurseData.id.toString(),
              partialPurseData.programId
            ),
          })
          queryClient.invalidateQueries({
            queryKey: PURSE_KEYS.PURSE_AFFILIATIONS(
              partialPurseData.id,
              partialPurseData.programId
            ),
          })
          toast({
            title: 'Success',
            description: 'Purse affiliation restriction updated successfully',
          })
        },
        onError: (error) => {
          const parsedError = parseRequestError(error)
          toast({
            title: 'Error',
            description: parsedError.message,
          })
        },
      }
    )
  }

  return (
    <Card>
      <CardHeader>
        <div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">
            Affiliation Restriction
          </h3>
          <p className="mt-1 text-sm text-gray-700">
            Enable SPEND usage of funds to be used on selected affiliations
            <br />
            <b>
              Disabling allows applicants awarded by this purse to freely spend
              funds.
            </b>
          </p>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center space-x-2">
          <Switch
            aria-label="Restrict spending to selected affiliations"
            disabled={updateMutation.status === 'pending'}
            checked={partialPurseData.affiliationRestrictionEnabled}
            onCheckedChange={handleUpdate}
          />

          <div className="ml-2 flex items-center text-sm font-medium text-gray-800">
            Restrict spending to selected affiliations
            {updateMutation.status === 'pending' && (
              <span className="ml-2">
                <CircleLoader className="fill-text-950" size="sm" />
              </span>
            )}
          </div>
        </div>

        <div className="mt-4 border-t border-gray-200 pt-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-gray-900">
                Selected Affiliations
              </p>
              <p className="text-sm text-gray-600">
                {purseAffiliationsQuery.status === 'pending'
                  ? 'Loading affiliations...'
                  : purseAffiliationsQuery.status === 'error'
                    ? 'Unable to load selected affiliations.'
                    : currentAffiliations.length === 0
                      ? 'No affiliations selected.'
                      : `${currentAffiliations.length} affiliation(s) selected.`}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setManageDialogOpen(true)}
              disabled={!hasLoadedCurrentAffiliations}
            >
              Manage Purse Affiliations
            </Button>
          </div>

          {purseAffiliationsQuery.status === 'error' && (
            <p className="mt-3 text-sm text-red-600">
              {purseAffiliationsQuery.error.message}
            </p>
          )}

          {currentAffiliations.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {currentAffiliations.map((affiliation) => (
                <div
                  key={affiliation.id}
                  className="rounded-md border border-gray-200 px-3 py-1 text-sm text-gray-700"
                >
                  {affiliation.name}
                </div>
              ))}
            </div>
          )}
        </div>
        <ManageAffiliationsDialog
          open={manageDialogOpen}
          onOpenChange={setManageDialogOpen}
          partialPurseData={partialPurseData}
          currentAffiliations={currentAffiliations}
        />
      </CardContent>
    </Card>
  )
}
